// Map ITU country-calling-codes to ISO-3166 alpha-2, then to flag emoji.
// Built once on the FE so we don't ship libphonenumber's full data.
// Backend supplies `customer_phone_country_code` (e.g. "971") on every
// new write — this util just picks the right flag.

// Calling code → ISO-3166-2. Long-form codes first (e.g. "1242"
// Bahamas vs "1" US) so longest-prefix-wins.
const CC_TO_ISO = {
  // North America
  '1': 'US',
  // SAARC + Middle East — most common Opswarex tenants
  '91': 'IN', '92': 'PK', '93': 'AF', '94': 'LK', '95': 'MM',
  '880': 'BD', '977': 'NP', '975': 'BT', '960': 'MV',
  '971': 'AE', '966': 'SA', '973': 'BH', '974': 'QA', '968': 'OM', '965': 'KW',
  '962': 'JO', '961': 'LB', '963': 'SY', '964': 'IQ', '967': 'YE', '972': 'IL',
  // Africa
  '20': 'EG', '27': 'ZA', '212': 'MA', '213': 'DZ', '216': 'TN', '218': 'LY',
  '234': 'NG', '254': 'KE', '255': 'TZ',
  // Europe
  '44': 'GB', '33': 'FR', '49': 'DE', '34': 'ES', '39': 'IT', '31': 'NL',
  '32': 'BE', '41': 'CH', '43': 'AT', '45': 'DK', '46': 'SE', '47': 'NO',
  '48': 'PL', '353': 'IE', '351': 'PT', '30': 'GR', '36': 'HU', '420': 'CZ',
  '7': 'RU',
  // East Asia
  '81': 'JP', '82': 'KR', '86': 'CN', '852': 'HK', '853': 'MO', '886': 'TW',
  // SE Asia + Oceania
  '60': 'MY', '62': 'ID', '63': 'PH', '65': 'SG', '66': 'TH', '84': 'VN',
  '855': 'KH', '856': 'LA', '95': 'MM', '673': 'BN',
  '61': 'AU', '64': 'NZ',
  // LatAm
  '52': 'MX', '54': 'AR', '55': 'BR', '56': 'CL', '57': 'CO', '58': 'VE',
  '51': 'PE', '593': 'EC',
  // Turkey + neighbours
  '90': 'TR', '98': 'IR',
};

// ISO-2 → flag emoji via regional-indicator codepoints.
function isoToFlag(iso) {
  if (!iso || iso.length !== 2) return '';
  const A = 0x1F1E6;
  const upper = iso.toUpperCase();
  return String.fromCodePoint(
    A + upper.charCodeAt(0) - 65,
    A + upper.charCodeAt(1) - 65,
  );
}

/**
 * Return the country flag emoji for a calling code.
 * @param {string|number} cc — e.g. "971", "+91", 91
 * @returns {string} flag emoji, or "" if unknown
 */
export function flagFromCallingCode(cc) {
  if (!cc) return '';
  const digits = String(cc).replace(/\D/g, '');
  if (!digits) return '';
  // Longest prefix wins (so "1242" Bahamas beats "1" US, "44" UK
  // beats "4...", etc.). Try 4 → 3 → 2 → 1 digit prefixes.
  for (let len = Math.min(4, digits.length); len >= 1; len--) {
    const prefix = digits.slice(0, len);
    if (CC_TO_ISO[prefix]) {
      return isoToFlag(CC_TO_ISO[prefix]);
    }
  }
  return '';
}

/**
 * Pretty-format a phone for display: "🇦🇪 +971 58 599 6479"
 * @param {object} order — record with customer_phone + optional
 *                          customer_phone_country_code / _e164.
 * @param {string} fallback — value to render when no phone is set
 * @returns {string}
 */
export function formatPhoneWithFlag(order, fallback = '—') {
  if (!order) return fallback;
  const display = order.customer_phone_e164
                  || order.phone_e164
                  || order.customer_phone
                  || order.phone
                  || '';
  if (!display) return fallback;
  const cc = order.customer_phone_country_code || order.phone_country_code;
  const flag = flagFromCallingCode(cc) || flagFromCallingCode(display);
  return flag ? `${flag} ${display}` : display;
}

/**
 * Resolve a flag from a record using both an explicit country-code
 * field and the raw phone (which may already start with "+CC..." or
 * "CC..." digits). Returns "" when neither yields a known country.
 *
 * Useful for legacy/Shopify rows where `customer_phone_country_code`
 * was not stamped at write time but the raw phone still carries the
 * country code (e.g. "+919876543210"). We only attempt phone-based
 * inference when the phone clearly carries a country code — i.e. it
 * starts with "+" — to avoid mis-flagging bare 10-digit national
 * numbers (a stray Indian "9876543210" would otherwise resolve to
 * Iran via the "98" prefix).
 *
 * @param {object} record — order / lead / conversation / customer
 * @param {string} ccField — name of the explicit CC field
 * @param {string} phoneField — name of the raw phone field
 * @returns {string} flag emoji or ""
 */
export function flagForRecord(record, ccField = 'customer_phone_country_code', phoneField = 'customer_phone') {
  if (!record) return '';
  const direct = flagFromCallingCode(record[ccField]);
  if (direct) return direct;
  const e164 = record[`${phoneField}_e164`];
  if (e164 && String(e164).startsWith('+')) {
    const f = flagFromCallingCode(e164);
    if (f) return f;
  }
  const raw = record[phoneField];
  if (raw && String(raw).startsWith('+')) {
    return flagFromCallingCode(raw);
  }
  return '';
}

// React component — convenient wrapper for use inline in JSX.
import React from 'react';

export const PhoneWithFlag = ({ record, fallback = '—', className = '' }) => {
  const text = formatPhoneWithFlag(record, fallback);
  return (
    <span className={className} data-testid="phone-with-flag">
      {text}
    </span>
  );
};

export default flagFromCallingCode;
